/**
 * The **trigger bus** — gathers active `triggered` handlers off the frozen combatants at a
 * {@link import("./family").TriggerFamily}, evaluates their conditions, orders them by a
 * principled **generation comparator**, and interprets each into grounded steps (via
 * {@link interpretEffect}). The resolver calls this at the attack fire points (the Concussion
 * Missiles North Star); the encounter room calls it at the lifecycle points (`turn_start/end`,
 * `round_end`) where the duration bus and resource resets ride.
 *
 * **Generation order** (`decisions/0212` item 2, F2a): replay doesn't depend on it, because the
 * step list records order, but generation needs one rule. Within one event:
 *   1. the event subject's own listeners first (`FireContext.primaryTarget`); at a turn edge
 *      (`start_turn`/`end_turn`) the other characters' come first instead, because "effects
 *      caused by other characters always resolve first during a character's turn" (Core p.68);
 *   2. everyone else by **initiative** (position in `state.order`; an absent combatant last);
 *   3. **`priority`**, higher first;
 *   4. the canonical **gather order**: weapon/tag → pilot → status → system ({@link Handler.vector}),
 *      then the order the site gathered them.
 * One tuple key, so the order is total and deterministic whatever order a fire site gathers in.
 *
 * **Handler gathering is an injectable {@link HandlerSource}.** Production supplies the overlay
 * loader's gather off the frozen combatants' equipped items / active statuses / tags; a test
 * hands it a static list ({@link staticHandlers}) without touching the bus. Either way the
 * source returns *all* handlers and the bus does the filtering.
 *
 * **Every fire is an event** (`decisions/0212`, `events/loop.ts`): {@link fireTrigger} raises its
 * family's event into the caller's event loop, and the gather-filter-sort-interpret pass above is
 * that event's trigger stage.
 */

import { resourceCapRawKey, type Cell, type DamageType, type EncounterState, type ResolvedStep } from "@lancer-vtt/domain";
import type { ForcedAttackRequest, TriggeredEffect } from "./atoms";
import { evaluatePredicate, type TriggerEvent } from "./predicate";
import { evaluateMatch, type TriggerFamily } from "./family";
import { affordable } from "../events/gameEvents";
import {
  interpretEffect,
  predicateContextFrom,
  type ChoiceSource,
  type EffectContext,
} from "./interpret";
import type { DiceSource } from "../../dice";
import type { LoadedOverlay } from "./loader";
import type { ResolutionLedger } from "./resolutionLedger";
import type { MovePath } from "./spatialQuery";
import type { InterceptSink } from "../nonAttackIntercept";
import { createEventLoop, type EventLoop, type FamilyEvent } from "../events/loop";

/**
 * One gathered handler: a `triggered` effect bound to the combatant it acts *from*
 * (`source` = the effect's owner = `self` when interpreted). The bus binds `target`
 * from the firing context (the attack's target at `on_hit`; the carrier at a
 * lifecycle point).
 */
/** The carrier vector a handler was gathered off, which sets its place in the F2a gather order. */
export type HandlerVector = "weapon" | "pilot" | "status" | "system";

export type Handler = {
  /** The combatant this effect belongs to (its `self`/`source`). */
  source: string;
  /** The vector it was gathered off; a hand-built handler without one sorts with the weapon vector. */
  vector?: HandlerVector;
  /** The triggered effect (carries its `on`, `match`, `condition`, `priority`, `effect`). */
  effect: TriggeredEffect;
  /**
   * The bound partner combatant, when this
   * handler was gathered off a status INSTANCE carrying `StatusInstance.partner` (Argonaut
   * Shield's planted status, `partner` = the Drake). Populated by `gatherStatusHandlers`
   * (`loader.ts`) only (the status-scan gather); every other gather (weapon/tag/pilot) leaves it
   * `undefined` — those sources carry no per-instance relationship.
   */
  partner?: string;
  /**
   * The reaction's own **named-cap key**, when this handler's
   * {@link TriggeredEffect.name} is set (or, absent that, the carrying status/entity's own
   * name/id — {@link import("./loader").gatherStatusHandlers} populates the fallback as
   * `e.name ?? ref.name`). Read by
   * the protector-gather compiler ({@link import("../reactionRegistry").gatherReaction}) as the
   * `spend_reaction`/`canUseReaction` key. **Populated for a pilot/system-vector handler too**
   * (`npc-features-fielded-roster` Phase 7.2, {@link import("./loader").handlersFromRefs}'s own
   * `ref.name` fold — a bare entry id, not the per-effect fallback the status vector computes),
   * so `compileAuthoredOffers`'s npc-feature gather has a real per-feature reaction-budget key —
   * two different `npcf_*` reactions on the same carrier (a future roster) key their own 1/round
   * slot off their own feature id, rather than colliding on the carrier's bare combatant id.
   * `undefined` only for a gather that carries no reaction-budget concept at all (a weapon/tag
   * handler).
   *
   * **Still not the same CONTRACT as {@link entityId} below, even though `handlersFromRefs` now
   * gives them the same value.** `resolution-narration` 1.2 needed the carrying entity's id for a
   * display use (the trigger bus's `EffectContext.firing`) and first got it by populating THIS
   * field off `handlersFromRefs` — which that lane's review rejected, because
   * `toHitReactionRegistry.ts`'s `compileSelfRoutedMissReactionOffers` reads
   * `h.effect.name ?? h.name ?? h.source` as the REACTION-CAP key for an un-named optional
   * pilot-effect on-miss clause, and populating `name` would silently renamespace that cap from
   * the carrier's own combatant id to the entity id. Phase 7.2 later made exactly that change
   * deliberately, arguing the per-feature key is the better behavior, and shipped it. The
   * reconcile keeps Phase 7.2's semantics here and keeps {@link entityId} as the separate,
   * display-only channel: this field is a reaction-budget key whose value its owner may change
   * again, `entityId` is pinned to "the carrying entity's id". A display need must never be the
   * reason this one moves.
   */
  name?: string;
  /**
   * **Display-only** carrying-entity id (`resolution-narration` 1.2) — the pilot-effect/talent
   * or system id this handler was gathered off (`t_crack_shot:2`, `ms_hunter_lock`), for naming
   * a fired clause in the trigger bus's `EffectContext.firing` (below) and, through it,
   * `interpretExtraDamage`'s `DamageStep.source.name`. Populated by {@link
   * import("./loader").handlersFromRefs} (the pilot-effect and system vectors) from the
   * gathered `CarrierRef.name`; `undefined` for a handler gathered any other way (the status
   * vector already carries its own name in {@link name} above; the raw weapon-tag gather,
   * `gatherHandlers`, doesn't populate this field either).
   *
   * **This is a SEPARATE field from {@link name}, and the separation is about CONTRACT, not
   * value — read this before "simplifying" a display-id need back onto `name`.** Since
   * `npc-features-fielded-roster` Phase 7.2, `handlersFromRefs` populates BOTH off `ref.name`, so
   * the two currently hold the same string on this vector. They still are not interchangeable.
   * `toHitReactionRegistry.ts`'s `compileSelfRoutedMissReactionOffers` computes its
   * reaction-budget key as `h.effect.name ?? h.name ?? h.source`, so `name`'s value IS what
   * `reactionAvailable`/`canUseReaction` count a per-turn allowance against — Phase 7.2 moved
   * that key from the carrier's combatant id to the entry id on purpose, and its owner may move
   * it again. `entityId` is pinned to "the carrying entity's id" for display and no
   * reaction-budget consumer may read it. `resolution-narration` 1.2 is the cautionary case: it
   * wanted a display id, populated `name` to get one, and silently renamespaced that cap as a
   * side effect — the change was right for Phase 7.2's reasons and wrong for 1.2's. So: a
   * reaction-budget consumer may ONLY ever read {@link name}; anything that wants a display id
   * off a pilot-effect/system handler reads `entityId`, and never argues about `name`'s value.
   */
  entityId?: string;
  /**
   * The carrying **`StatusInstance`'s own `source`** — the applying
   * combatant (`@lancer-vtt/domain`'s `StatusInstance.source`), when this handler was gathered
   * off a status instance carrying one. Populated by {@link import("./loader").
   * gatherStatusHandlers} only (the status-scan gather) — every other gather (weapon/tag/pilot/
   * system) leaves it `undefined`, mirroring {@link partner}'s own status-instance-only scope.
   * NOT the same thing as {@link source} above: `source` here is the *carrier* (whoever holds
   * the status), `carrierSource` is the *caster* who applied it — a third-party reaction's
   * decider is `carrierSource`, never `source`. A caller that fires this handler threads it onto
   * the fired {@link import("./interpret").EffectContext}/{@link
   * import("./predicate").PredicateContext} as `carrierSource` so a `has_status` with `source: "carrier"`
   * predicate (or a checkpoint consumer routing an offer) can reach the caster without a fourth
   * `EffectContext.actors` slot.
   */
  carrierSource?: string;
}

/** The gather seam: returns *all* active handlers — the bus filters by family, match, and condition. */
export type HandlerSource = {
  /** Every active triggered handler in the encounter (the bus filters/orders). */
  handlers(state: EncounterState): Handler[];
}

/** The context a trigger firing binds against (the firing event's actors + inputs). */
export type FireContext = {
  state: EncounterState;
  /** The combatant the event centers on (the attack's target at `on_hit`; the carrier at turn_start). */
  primaryTarget?: string;
  /** The combatant who caused the event (the attacker at `on_hit`). */
  attacker?: string;
  dice: DiceSource;
  /** Answers `choose` atoms and `gm_ruling` predicates (see `choices.ts`). */
  choices?: ChoiceSource;
  /** Default save DC for `force_save` atoms (the attacker's frozen `saveTarget`, supplied by the caller). */
  defaultSaveDc?: number;
  /**
   * The **area template center** cell — threaded onto the fired handler's
   * {@link EffectContext} as `site`, so a Blast/Burst-Knockback rider's `forced_move away_from: "site"`
   * pushes each target radially away from the blast center. `undefined` for a single-target event.
   */
  site?: Cell;
  /**
   * **Does this firing belong to an attack that targets more than one character?** Threaded
   * straight onto the fired handler's {@link EffectContext} as `areaAttack`, where it supplies
   * the second half of RAW's bonus-damage halving (Core p.67 / p.78) — an `extra_damage` atom
   * flagged `bonusDamage: true` halves when, and only when, this is `true`. `undefined`/`false`
   * for every single-target firing and every non-attack lifecycle point.
   *
   * Set by the three attack-family fire sites in `resolve/`, each from the fact IT holds:
   * the two resolved-phase fires (`fireHitTriggers`/`fireMissTriggers`) read
   * `sharedDamage !== undefined` — the area fan-out's own marker, so a reaction or an
   * unanchorable template that degrades to the single-target spine correctly reads `false` —
   * and the declared-phase fire (`fireAttackDeclared`) reads `resolve()`'s own
   * `!intent.reaction && isAreaRange(range)` dispatch predicate, the only area fact that
   * exists that early.
   */
  areaAttack?: boolean;
  /** **This attack can't deal bonus damage** (a weapon's own secondary attack, the Annihilator's): onto {@link EffectContext} as `noBonusDamage`. */
  noBonusDamage?: boolean;
  /** **The type this attack's bonus damage takes** (Walking Armory's HELLFIRE): onto {@link EffectContext} as `bonusDamageType`. */
  bonusDamageType?: DamageType;
  /**
   * **How many times heat this attack adds to its target is halved** (`target` the attacked
   * combatant): once for Resistance the target has against this attack (a Brace, unless Shredded),
   * once for a `damageScale: "half"` attack (Covering Fire). RAW Brace resists "all damage, heat,
   * and burn from the triggering attack", so an on-hit clause's `add_heat` halves with it, rounding
   * up. Onto {@link EffectContext} as `heatHalvings`; set only by `fireHitTriggers`.
   */
  heatHalvings?: { target: string; times: number };
  /**
   * **The attacked combatant accepted a reaction that resists this attack** (a Brace, unless
   * Shredded). RAW Brace resists "all damage, heat, and burn from the triggering attack", so an
   * on-hit `extra_damage` against `target` is halved, rounding up. Onto {@link EffectContext} as
   * `reactionResisted`; set only by `fireHitTriggers`.
   */
  reactionResisted?: { target: string };
  /**
   * **The Armor the attack's own damage left unused against `target`** (BC-019). RAW Core p.67
   * subtracts Armor once from an attack's total, so an `extra_damage` against `target` pays only
   * `left`, and draws it down. One object for the whole fire, shared by reference, so two riders
   * cannot both spend it. Onto {@link EffectContext} as `attackArmor`; set only by `fireHitTriggers`.
   */
  attackArmor?: { target: string; left: number };
  /**
   * **How many characters this attack targets** — threaded straight onto the fired handler's
   * {@link EffectContext} as `targetCount`, where it is the `{TARGET_COUNT}` magnitude
   * binding's one source, and onto the {@link event} payload's own `targetCount` so a
   * `match: { targetCount: { ">": 1 } }` gate reads the SAME number the magnitude scales by.
   *
   * The scalar twin of {@link areaAttack} above (which stays a boolean about the resolution
   * SHAPE, for the bonus-damage halving) — set by the same three attack-family fire sites, each
   * from the fact IT holds: the two resolved-phase fires (`fireHitTriggers`/`fireMissTriggers`)
   * read `resolveSingleTarget`'s own count — the area fan-out's settled `targetIds.length`
   * threaded down per sub-resolution, or `1` when nothing threaded one — and the declared-phase
   * fire (`fireAttackDeclared`) sets `1` ONLY when it already knows the attack is not fanning
   * out, and omits it otherwise: at declare, an area attack's template has not been enumerated
   * or LoS-filtered, so the count genuinely does not exist yet. `undefined` for every non-attack
   * lifecycle point.
   */
  targetCount?: number;
  /**
   * Threaded straight onto the fired handler's {@link EffectContext} — set by the caller when
   * this firing IS a tech spine (INVADE's on-hit FRAGMENT SIGNAL), so the emitted `add_status`
   * steps carry `cause: "tech"`. `undefined` for every non-tech trigger (Concussion Missiles,
   * lifecycle points, …).
   */
  statusCause?: "tech";
  /**
   * The overlay to fold a `force_save` atom's carrier-self save/check modifiers from
   * (Impaired/Bolster reaching a forced save fired mid-attack, e.g. Concussion Missiles' HULL
   * save). Threaded straight onto the fired handler's {@link EffectContext}; absent →
   * `SEED_OVERLAY` (interpret.ts's default).
   */
  overlay?: LoadedOverlay;
  /**
   * The typed **per-family event payload** — ONE typed record per firing (`AttackEvent`/
   * `ActionEvent`/`MoveEvent`, `predicate.ts`), threaded straight onto the fired handler's
   * {@link EffectContext}. The caller sets it ONLY at the fire point the payload's family
   * belongs to (`fireAttackDeclared`/the on_hit-on_crit/on_miss firing for {@link
   * import("./predicate").AttackEvent}, `resolveActionTakenTrigger` for {@link
   * import("./predicate").ActionEvent}, `resolveMoveTrigger` for {@link
   * import("./predicate").MoveEvent}); every other caller omits it, which reads as no match for
   * every event-fact predicate op on the fired handler's `condition` — so a weapon-gated
   * `triggered` clause on `turn_start`/`turn_end`/`round_start`/`round_end` stays correctly dark.
   */
  event?: TriggerEvent;
  /**
   * The resolution-scoped **fact ledger** — threaded straight
   * onto the fired handler's {@link EffectContext} exactly like `overlay`/`statusCause`
   * above. Unlike every other field on this type (a frozen fact, recomputed identically at
   * each fire site), this is the ONE mutable, by-reference-shared object: the SAME ledger
   * the caller created once for the whole `resolve()` call, so a `choose` atom's decision
   * recorded at an EARLIER fire point (`on_attack_declared`) is readable by a `chose`
   * predicate on a clause fired HERE. `undefined` for a caller outside an attack resolution
   * (a lifecycle point) — a `chose` predicate then reads `false`. See {@link
   * import("./resolutionLedger").ResolutionLedger} for the gap this closes.
   */
  ledger?: ResolutionLedger;
  /**
   * The recorded **direction** cell — threaded straight onto the fired handler's
   * {@link EffectContext} as `toward`, mirroring `site`/`overlay` above.
   * Populates a `move_self_toward_target{toward:"chosen"}` clause's anchor when the caller has a
   * recorded answer in hand (`resolve/attackTriggerFires.ts`'s on_miss fire site reads `intent.reactionToward`).
   * `undefined` when no direction has been recorded yet (the halt case below reads `undefined`).
   */
  toward?: Cell;
  /** Recorded placement cells, threaded onto {@link EffectContext} as `sites` (an attack's `RequestAttack.sites`). */
  sites?: Cell[];
  /** A two-space mark's other space, threaded onto {@link EffectContext} as `pair` (`resolveAreaTrigger`). */
  pair?: Cell;
  /**
   * Whether this firing is the server's **live interactive round-trip** (mirrors
   * `EffectsConfig.interactive`/`ChoiceSource.requireAnswer`) — threaded onto the fired handler's
   * {@link EffectContext} so an atom that needs a spatial answer (`move_self_toward_target
   * {toward:"chosen"}` with no recorded `toward`) can halt with a {@link
   * import("./interpret").NeedsDecisionSignal} instead of silently no-op'ing. `undefined`/`false`
   * (replay, a scripted fixture, the CLI's non-interactive default) preserves the faithful
   * no-roundtrip no-op.
   */
  interactive?: boolean;
  /**
   * Whether a SPATIAL answer can actually get back to this resolution — threaded onto the fired
   * handler's {@link EffectContext} as `directionAnswerable` (see that field's own doc for the
   * full account). `false` for a caller whose echoed intent shape carries no `reactionToward`
   * slot, which makes the spatial halt degrade instead of raising a prompt
   * nobody could ever answer. `undefined` reads as answerable.
   */
  directionAnswerable?: boolean;
  /**
   * The sink a fired `forced_attack` queues onto ({@link EffectContext.forcedAttacks}), threaded
   * by reference. A fire site that sets it hands the queue to the referee, which resolves it
   * (`referee/forcedAttacks.ts`). Unset, the atom degrades to its "resolve it by hand" advisory.
   */
  forcedAttacks?: ForcedAttackRequest[];
  /** A fired `damage` atom's Intercede answers and spend sink ({@link EffectContext.intercede}). */
  intercede?: InterceptSink;
  /**
   * The intent's event loop (`events/loop.ts`), when the caller holds one. Absent, the fire uses
   * the one on {@link ledger}, else a loop of its own over {@link dice}.
   */
  loop?: EventLoop;
}

/** The result of firing a trigger point: the concatenated steps + warnings, in generation order. */
export type FireResult = {
  steps: ResolvedStep[];
  warnings: string[];
  /**
   * `modifier_consume_resource_on_apply` — every capped `TriggeredEffect` handler that ACTUALLY
   * fired this point (its cadence cap held).
   * The carrier (`Handler.source`) + raw `key` the caller turns into a `resource_delta` +1 step,
   * spliced onto the caller's own `Resolved.resourceCapConsumeSteps` at the ROOM layer — mirrors
   * `ModifierContributionSum.consumedResourceCaps`'s own doc. Empty when nothing fired carried
   * the facet (the overwhelming common case today).
   */
  consumedCaps: { carrier: string; key: string }[];
  /** The walk a fired `move_self_toward_target` made (Puppet System's), for the referee's move checkpoint and hazards. The last one wins. */
  movePath?: MovePath;
}

/** F2a's gather order within one carrier: weapon/tag → pilot → status → system. */
const VECTOR_RANK: Record<HandlerVector, number> = { weapon: 0, pilot: 1, status: 2, system: 3 };

/** The turn edges, where the other characters' listeners run before the turn-taker's (Core p.68). */
const OTHERS_FIRST: ReadonlySet<TriggerFamily> = new Set<TriggerFamily>(["start_turn", "end_turn"]);

/**
 * Sort indexed handlers into the F2a order (this module's doc), one tuple key each: subject group,
 * initiative, priority (higher first), gather vector, then the site's gather index.
 */
function inF2aOrder(handlers: readonly { h: Handler; idx: number }[], args: { state: EncounterState; subject: string | undefined; family: TriggerFamily }): { h: Handler; idx: number }[] {
  const { state, subject, family } = args;
  const ownFirst = !OTHERS_FIRST.has(family);
  const keyed = handlers.map((x) => {
    const at = state.order.indexOf(x.h.source);
    const own = subject !== undefined && x.h.source === subject;
    return { x, key: [subject === undefined ? 0 : own === ownFirst ? 0 : 1, at === -1 ? Number.MAX_SAFE_INTEGER : at, -(x.h.effect.priority ?? 0), VECTOR_RANK[x.h.vector ?? "weapon"], x.idx] };
  });
  keyed.sort((a, b) => {
    for (let i = 0; i < a.key.length; i++) if (a.key[i] !== b.key[i]) return a.key[i]! - b.key[i]!;
    return 0;
  });
  return keyed.map(({ x }) => x);
}

/**
 * Fire a {@link TriggerFamily}: gather the active handlers, keep those whose `on` matches
 * `family`, then whose
 * `match` (if any) evaluates true against `fire.event` ({@link evaluateMatch}), then
 * whose `condition` (if any) holds — sort by the generation comparator, and interpret
 * each into grounded steps, concatenated in order. The `self` of each handler is its
 * `source`; `target`/`attacker` come from the {@link FireContext}. (For a lifecycle
 * point like `turn_start`, the carrier is both `self` and the natural `target`.)
 *
 * **Filter order is load-bearing** (`family.ts`'s `evaluateMatch` own doc, the
 * double-fire-avoidance mechanism): family → match → condition → cap. A handler whose
 * family doesn't match is never even checked against `match`/`condition` — cheap, and
 * keeps a stray same-family-different-phase clause (e.g. an `on:"attack"` clause fired
 * at the declared moment when it only matches `hit:true`) a clean `match` miss, not a
 * `condition`-evaluation surprise.
 */
export function fireTrigger(
  family: TriggerFamily,
  source: HandlerSource,
  fire: FireContext,
): FireResult {
  const loop = fire.loop ?? fire.ledger?.loop ?? createEventLoop({ dice: fire.dice });
  const event: FamilyEvent = {
    kind: "family",
    family,
    ...(fire.event !== undefined ? { payload: fire.event } : {}),
    ...(fire.primaryTarget !== undefined ? { subject: fire.primaryTarget } : {}),
    ...(fire.attacker !== undefined ? { actor: fire.attacker } : {}),
  };
  // No replacement listens to a family event yet, so it always lands.
  return loop.raise(event, { trigger: () => runTriggers(family, source, { ...fire, loop }) })!;
}

/**
 * The trigger stage of a family event: the listeners `source` gathers, in generation order. Every
 * fire site passes its own gather today, the explicit override of {@link
 * import("./listeners").listenersFor}'s canonical order that kernel Phase 6 removes.
 */
function runTriggers(family: TriggerFamily, source: HandlerSource, fire: FireContext): FireResult {
  const loop = fire.loop ?? createEventLoop({ dice: fire.dice });
  const all = source.handlers(loop.scratch(fire.state));
  const matching = all
    .map((h, idx) => ({ h, idx }))
    .filter(({ h }) => h.effect.on === family)
    .filter(({ h }) => h.effect.match === undefined || evaluateMatch(h.effect.match, fire.event));

  const steps: ResolvedStep[] = [];
  const warnings: string[] = [];
  const consumedCaps: { carrier: string; key: string }[] = [];
  let movePath: MovePath | undefined;

  for (const { h } of inF2aOrder(matching, { state: fire.state, subject: fire.primaryTarget, family })) {
    const ctx: EffectContext = {
      // The scratch state: the stage's entry state with every step landed since folded in, so this
      // listener reads what the resolution and the listeners before it did (`decisions/0212` item 4).
      state: loop.scratch(fire.state),
      actors: {
        self: h.source,
        // At a lifecycle point with no explicit target, the handler's owner is the target.
        // **Deliberately kept, unlike `attacker`'s (deleted) fallback.** This one is
        // load-bearing beyond this file: `resolveActor` (`interpret.ts`) resolves an atom's
        // *omitted* `target` as `ctx.actors.target ?? ctx.actors.self`, so every turn-start
        // `add_heat`/`heal` with no explicit `target` rides it. The asymmetry is the
        // point — `target` here is a *designed* binding with a stated meaning ("the
        // carrier" at a non-attack family); an `attacker` outside attack/damage/save has no
        // such meaning. `coverage.ts`'s `FAMILY_BOUND_ROLES` is the calibrated table.
        target: fire.primaryTarget ?? h.source,
        // No fallback here — an unbound `attacker` (any family
        // outside `attack`/`damage`/`save`, none of whose fire sites in `resolve/`/
        // `interpret.ts` set `fire.attacker`) resolves to `undefined`, which the closed
        // predicate evaluator reads as `false` and the atom interpreter as a skipped
        // recipient. Never silently aliases to `self`.
        attacker: fire.attacker,
      },
      dice: fire.dice,
      choices: fire.choices,
      defaultSaveDc: fire.defaultSaveDc,
      // The area template center, so `forced_move away_from: "site"` resolves.
      ...(fire.site !== undefined ? { site: fire.site } : {}),
      // `area-bonus-damage-halving` Phase 1: the area-mode fact, so a fired `extra_damage`
      // flagged as RAW BONUS DAMAGE halves on an attack that targets more than one character.
      // Deliberately NOT derived from `fire.site` — a Burst/Blast site is recorded for a
      // single-target-degraded resolution too, and `site` says "where the template centered",
      // not "this attack targets more than one character".
      ...(fire.areaAttack !== undefined ? { areaAttack: fire.areaAttack } : {}),
      ...(fire.noBonusDamage === true ? { noBonusDamage: true } : {}),
      ...(fire.bonusDamageType !== undefined ? { bonusDamageType: fire.bonusDamageType } : {}),
      ...(fire.heatHalvings !== undefined ? { heatHalvings: fire.heatHalvings } : {}),
      ...(fire.reactionResisted !== undefined ? { reactionResisted: fire.reactionResisted } : {}),
      ...(fire.attackArmor !== undefined ? { attackArmor: fire.attackArmor } : {}),
      // The `{TARGET_COUNT}` binding's source — the scalar beside `areaAttack`'s boolean. Same
      // omit-when-absent discipline: a firing that doesn't know the count must not claim one, so
      // the magnitude resolver can degrade honestly (and warn) rather than silently read 1.
      ...(fire.targetCount !== undefined ? { targetCount: fire.targetCount } : {}),
      // The tech-spine tag, onto every fired handler.
      ...(fire.statusCause !== undefined ? { statusCause: fire.statusCause } : {}),
      // The overlay, so a fired `force_save` folds the acting combatant's own save/check
      // modifiers (Impaired/Bolster).
      ...(fire.overlay !== undefined ? { overlay: fire.overlay } : {}),
      // The typed per-family event payload, so a `triggered` clause's `condition` can gate on
      // its event facts (see `FireContext`'s own doc).
      ...(fire.event !== undefined ? { event: fire.event } : {}),
      // The resolution-scoped fact ledger, so a fired handler's own `condition` can read a
      // same-resolution `chose` predicate.
      ...(fire.ledger !== undefined ? { ledger: fire.ledger } : {}),
      // The recorded direction + interactive-round-trip flag, so a fired
      // `move_self_toward_target{toward:"chosen"}` can read/halt on them.
      ...(fire.toward !== undefined ? { toward: fire.toward } : {}),
      ...(fire.sites !== undefined ? { sites: fire.sites } : {}),
      ...(fire.pair !== undefined ? { pair: fire.pair } : {}),
      ...(fire.interactive !== undefined ? { interactive: fire.interactive } : {}),
      ...(fire.directionAnswerable !== undefined ? { directionAnswerable: fire.directionAnswerable } : {}),
      ...(fire.forcedAttacks !== undefined ? { forcedAttacks: fire.forcedAttacks } : {}),
      ...(fire.intercede !== undefined ? { intercede: fire.intercede } : {}),
      ...(fire.loop !== undefined ? { loop: fire.loop } : {}),
      // The currently-firing clause's own name + carrier (`resolution-narration` 1.2), so
      // `interpretExtraDamage` can stamp a rider `damage` step's provenance. Order:
      // `h.effect.name` (the authored reaction-cap name, when the clause set one directly),
      // else `h.entityId` (the DISPLAY-ONLY carrying-entity id `handlersFromRefs` populates for
      // the pilot-effect/system vectors — `Handler.entityId`'s own doc), else `h.name` (a status
      // handler's own name/id — `statusHandlersFromRefs` — safe to read here since `h.name`
      // reverted to its pre-1.2, status-only scope; never populated for pilot/system). All three
      // can be absent (the raw weapon-tag gather, `gatherHandlers`, or a handler shape that
      // skipped the registry-vector reducers, e.g. `gatherHandlersForLostStatus`'s bare
      // `{ source, effect }`) — `firing` is then omitted entirely, NEVER falling back to `h
      // .source` (a bare combatant id), which would misname an unnamed rider after whoever
      // fired it rather than honestly recording that this emitter has no name to give.
      ...(h.effect.name ?? h.entityId ?? h.name ? { firing: { name: (h.effect.name ?? h.entityId ?? h.name)!, carrier: h.source } } : {}),
    };
    // The condition reads the scratch state: every step landed ahead of this listener is folded in.
    if (h.effect.condition) {
      if (!evaluatePredicate(h.effect.condition, predicateContextFrom(ctx))) continue;
    }
    // A self-optional "may" rider gates HERE — above the `consumeResourceOnApply` check below —
    // because that check pushes onto `consumedCaps` before `interpretEffect` ever runs. Gating
    // below the cap check would still use up a capped clause's cadence on a decline
    // (`t_walking_armory:1`'s 1/turn "you may expend charges" would count as used even when
    // declined). The resolver raises each consumed cap as a cost event (`events/gameEvents.ts`). The recorded answer is read at `optIn:<name>`; the
    // affirmative encoding is the literal string `"true"` — `choose` only returns a value that's
    // among the offered `optionIds`, so anything else (a different string, or the key simply
    // never having been recorded) already falls through to `undefined` for free. A clause with
    // no `name` (the lint rule should have caught that at authoring time, but the engine still
    // has to behave) ALSO declines — fail closed, not a crash and not some other fallback key.
    if (h.effect.requiresOptIn) {
      const name = h.effect.name;
      if (!name || fire.choices?.choose(`optIn:${name}`, ["true"]) !== "true") continue;
    }
    // `modifier_consume_resource_on_apply`: a capped triggered clause gates independently of
    // `condition` — at (or past) the cadence
    // cap it doesn't fire at all this point (centimane's on-crit save-or-debuff, capped 1/round).
    // Checked AFTER `condition` so an authored gate composes correctly with the automatic cap.
    if (h.effect.consumeResourceOnApply) {
      const cap = h.effect.consumeResourceOnApply;
      if (!affordable({ kind: "resource_cap_use", carrier: h.source, key: resourceCapRawKey(cap), max: cap.max }, ctx.state, fire.ledger)) continue;
      consumedCaps.push({ carrier: h.source, key: resourceCapRawKey(cap) });
    }
    const r = interpretEffect(h.effect.effect, ctx);
    loop.land(fire.state, r.steps);
    steps.push(...r.steps);
    warnings.push(...r.warnings);
    if (ctx.movePath !== undefined) movePath = ctx.movePath;
  }
  return { steps, warnings, consumedCaps, ...(movePath !== undefined ? { movePath } : {}) };
}

/** Build a {@link HandlerSource} from a static handler list. */
export function staticHandlers(handlers: Handler[]): HandlerSource {
  return { handlers: () => handlers };
}
